import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { useParams, useHistory } from "react-router-dom";
import {
  getAdminOrder,
  getAdminCouriers,
  reassignCourier,
  cancelOrder,
  addAdminOrderNote,
} from "../../api/adminApi";

export default function AdminOrderDetails() {
  const { orderId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [order, setOrder] = useState(null);
  const [couriers, setCouriers] = useState([]);

  const [selectedCourierId, setSelectedCourierId] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [noteText, setNoteText] = useState("");

  // Helpers to support different backend shapes
  const timeline = useMemo(() => {
    if (!order) return [];
    // backend could return: order.timeline OR order.status_events OR order.events
    return order.timeline || order.status_events || order.events || [];
  }, [order]);

  const pod = useMemo(() => {
    if (!order) return null;
    // backend could return: order.pod OR order.proof_of_delivery
    return order.pod || order.proof_of_delivery || null;
  }, [order]);

  const podPhotos = useMemo(() => {
    if (!pod) return [];
    // could be pod.photos or pod.images
    return pod.photos || pod.images || [];
  }, [pod]);

  const currentCourierId = useMemo(() => {
    if (!order) return "";
    return (
      order.courier_id ||
      order.courierId ||
      order.courier?.id ||
      ""
    );
  }, [order]);

  // Load order + couriers
  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [orderRes, couriersRes] = await Promise.all([
          getAdminOrder(orderId),
          getAdminCouriers(),
        ]);

        const orderData = orderRes?.data;
        const couriersData = couriersRes?.data;

        // Couriers list shape could be {items: []} or []
        const courierList = Array.isArray(couriersData)
          ? couriersData
          : couriersData?.items || couriersData?.couriers || [];

        if (!isMounted) return;

        setOrder(orderData);
        setCouriers(courierList);

        // Preselect current courier
        const cid =
          orderData?.courier_id ||
          orderData?.courierId ||
          orderData?.courier?.id ||
          "";
        setSelectedCourierId(cid ? String(cid) : "");
      } catch (e) {
        if (!isMounted) return;
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load order details.";
        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [orderId]);

  const handleReassign = async () => {
    if (!selectedCourierId) {
      setError("Please select a courier before reassigning.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await reassignCourier(orderId, selectedCourierId);
      // Reload order after action
      const refreshed = await getAdminOrder(orderId);
      setOrder(refreshed.data);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to reassign courier.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setError("Cancel reason is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await cancelOrder(orderId, cancelReason.trim());
      const refreshed = await getAdminOrder(orderId);
      setOrder(refreshed.data);
      setCancelReason("");
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to cancel order.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    setError("");
    try {
      await addAdminOrderNote(orderId, noteText.trim());
      const refreshed = await getAdminOrder(orderId);
      setOrder(refreshed.data);
      setNoteText("");
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to add note.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading order…</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Alert severity="error">
        Order not found or failed to load.
      </Alert>
    );
  }

  const displayOrderId = order.id || order.order_id || orderId;
  const displayStatus =
    order.status || order.order_status || order.orderStatus || "-";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button variant="outlined" onClick={() => history.goBack()}>
          Back
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Order #{displayOrderId}
        </Typography>

        <Typography sx={{ opacity: 0.7 }}>
          Status: {displayStatus}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* MAIN GRID */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {/* LEFT: Order + Timeline */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {timeline.length === 0 ? (
            <Typography sx={{ opacity: 0.7 }}>
              No status events found.
            </Typography>
          ) : (
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {timeline.map((e, idx) => {
                const status = e.status || e.state || e.type || "event";
                const t = e.created_at || e.createdAt || e.timestamp;
                return (
                  <li key={e.id || idx}>
                    <Typography>
                      <b>{status}</b>
                      {" — "}
                      {t ? new Date(t).toLocaleString() : "no timestamp"}
                    </Typography>
                  </li>
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Assign / Reassign Courier
          </Typography>

          <TextField
            select
            label="Courier"
            value={selectedCourierId}
            onChange={(e) => setSelectedCourierId(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          >
            <MenuItem value="">
              <em>Select courier</em>
            </MenuItem>
            {couriers.map((c) => {
              const id = c.id || c.courier_id || c.courierId;
              const name =
                c.name ||
                c.full_name ||
                c.fullName ||
                `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                `Courier ${id}`;
              return (
                <MenuItem key={id} value={String(id)}>
                  {name}
                  {String(id) === String(currentCourierId) ? " (current)" : ""}
                </MenuItem>
              );
            })}
          </TextField>

          <Button
            variant="contained"
            onClick={handleReassign}
            disabled={saving}
          >
            {saving ? "Saving…" : "Reassign Courier"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Cancel Order
          </Typography>

          <TextField
            label="Cancel reason (required)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={saving}
          >
            {saving ? "Saving…" : "Cancel Order"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Internal Admin Notes
          </Typography>

          <TextField
            label="Add note (append-only)"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 1 }}
          />
          <Button variant="outlined" onClick={handleAddNote} disabled={saving}>
            Add Note
          </Button>

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              Existing Notes
            </Typography>
            {(order.admin_notes || order.notes || []).length === 0 ? (
              <Typography sx={{ opacity: 0.7 }}>No notes yet.</Typography>
            ) : (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {(order.admin_notes || order.notes || []).map((n, idx) => (
                  <li key={n.id || idx}>
                    <Typography>
                      {n.text || n.note || String(n)}
                      {" — "}
                      {n.created_at
                        ? new Date(n.created_at).toLocaleString()
                        : ""}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Box>
        </Paper>

        {/* RIGHT: POD */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Proof of Delivery (POD)
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {!pod ? (
            <Typography sx={{ opacity: 0.7 }}>
              No POD found for this order.
            </Typography>
          ) : (
            <Box>
              <Typography sx={{ mb: 1 }}>
                <b>Notes:</b> {pod.notes || pod.note || "-"}
              </Typography>

              <Typography sx={{ mb: 2 }}>
                <b>Delivered at:</b>{" "}
                {pod.delivered_at || pod.created_at
                  ? new Date(pod.delivered_at || pod.created_at).toLocaleString()
                  : "-"}
              </Typography>

              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Photos
              </Typography>

              {podPhotos.length === 0 ? (
                <Typography sx={{ opacity: 0.7 }}>No photos.</Typography>
              ) : (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {podPhotos.map((p, idx) => {
                    const url = p.url || p.photo_url || p.image_url || p;
                    return (
                      <Box key={idx} sx={{ border: "1px solid #ddd", p: 0.5 }}>
                        {/* basic image */}
                        <img
                          src={url}
                          alt="POD"
                          style={{ width: 160, height: "auto" }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
